import type {ReactElement} from 'react';
import type {MenuGroupListProps} from './menu-group-list.types';
import {IMAGE_SIZE_16} from './menu-group-list.types';
import type {
  ContextualMenuGroup,
  ContextualMenuItem,
  ContextualMenuItemWithEmoji,
  ContextualMenuItemWithIcon,
} from '@Components/ai-prompt-box/components/ai-prompt-box-footer-actions/ai-prompt-box-contextual-menu/ai-prompt-box-contextual-menu.types';
import {
  ContextualMenuGroupType,
  ContextualMenuItemGraphicType,
} from '@Components/ai-prompt-box/components/ai-prompt-box-footer-actions/ai-prompt-box-contextual-menu/ai-prompt-box-contextual-menu.types';
import styles from './menu-group-list.module.scss';
import {Text, TextSize} from '@Components/text';
import {PillButton} from '@Components/pill-button';
import type {PillButtonImageProps} from '@Components/pill-button/pill-button.types';
import {PillButtonSize} from '@Components/pill-button/pill-button.types';
import {LabelledCheckbox} from '@Components/labelled-checkbox';
import {Icon} from '@Components/icon-v2';
import {IconSize, IconType} from '@Components/icon-v2/icon.types';
import {Pill, PILL_BACKGROUND_COLOR, PILL_CONTENT_TYPE, PILL_SIZE} from '@Components/pill';
import {ClickableDiv} from '@Components/clickable-div';
import {assertNever} from '@Utils/assert-never.util';

export function MenuGroupList({groups, onItemClick}: MenuGroupListProps): ReactElement {
  const getGroupHeading = (group: ContextualMenuGroup): ReactElement => {
    return <Text bold val={group.label} size={TextSize.XXSMALL} className={styles.contextualMenuHeading} />;
  };

  const getItemGraphic = (item: ContextualMenuItem | ContextualMenuItemWithEmoji | ContextualMenuItemWithIcon): ReactElement | null => {
    if (!('graphicType' in item)) {
      return null;
    }

    switch (item.graphicType) {
      case ContextualMenuItemGraphicType.EMOJI:
        return <span>{item.emoji}</span>;
      case ContextualMenuItemGraphicType.ICON:
        if (item.icon) {
          return <Icon icon={item.icon} size={IconSize.SIZE_ICON_16} type={item.active ? IconType.PRIMARY : IconType.SECONDARY} />;
        }
        if (item.imageUrl) {
          return <img src={item.imageUrl} alt={item.label} width={IMAGE_SIZE_16} height={IMAGE_SIZE_16} />;
        }
        return null;
      default:
        return null;
    }
  };

  const getPillButton = (item: ContextualMenuItem, groupIndex: number, itemIndex: number): ReactElement => {
    const isIconItem = 'graphicType' in item && item.graphicType === ContextualMenuItemGraphicType.ICON;
    const iconItem = isIconItem ? (item as ContextualMenuItemWithIcon) : undefined;
    const leftIcon = iconItem?.icon ? {icon: iconItem.icon, size: IconSize.SIZE_ICON_16} : undefined;
    const leftImage: PillButtonImageProps | undefined = iconItem && !iconItem.icon && iconItem.imageUrl ? {src: iconItem.imageUrl, alt: item.label} : undefined;
    const emoji = 'graphicType' in item && item.graphicType === ContextualMenuItemGraphicType.EMOJI ? (item as ContextualMenuItemWithEmoji).emoji : undefined;
    const isSelected = item.active && !item.disabled;
    const displayText = emoji ? `${emoji} ${item.label}` : item.label;

    return (
      <PillButton
        key={item.value}
        text={displayText}
        isSelected={isSelected}
        size={PillButtonSize.SMALL}
        leftIconProps={leftIcon}
        leftImageProps={leftImage}
        isDisabled={item.disabled}
        onClick={() => {
          onItemClick(groupIndex, itemIndex);
        }}
        textSize={TextSize.XXSMALL}
      />
    );
  };

  function buildCardClassName(isActive: boolean, isDisabled: boolean): string {
    if (isDisabled) {
      return styles.card + ' ' + styles.disabled;
    }

    if (isActive) {
      return styles.card + ' ' + styles.selected;
    }

    return styles.card;
  }

  function buildLabelClassName(isActive: boolean, isDisabled: boolean): string {
    if (isDisabled) {
      return 'content-disabled-dark';
    }

    if (isActive) {
      return 'content-primary';
    }

    return '';
  }

  function buildHybridPillClassName(isActive: boolean): string {
    if (isActive) {
      return `${styles.pill} bg-primary-20`;
    }

    return `${styles.pill} bg-ai-light`;
  }

  function buildIconPillClassName(isActive: boolean): string {
    if (isActive) {
      return 'spacing-p-l-2 spacing-p-r-2 spacing-p-t-1 spacing-p-b-1 bg-primary-20 content-primary';
    }

    return 'spacing-p-l-2 spacing-p-r-2 spacing-p-t-1 spacing-p-b-1 bg-ai-light content-secondary';
  }

  function renderRightPill(item: ContextualMenuItem): ReactElement | null {
    const isActive = item.active;
    const isDisabled = item.disabled;
    const hasCredits = item.credits !== undefined && item.topRightIcon;

    if (hasCredits) {
      return (
        <Pill
          content={{
            type: PILL_CONTENT_TYPE.HYBRID,
            icon: item.topRightIcon ?? '',
            text: (item.credits ?? 0).toString(),
            isBold: true,
            textColor: isDisabled ? 'content-disabled' : isActive ? 'content-primary' : 'content-secondary',
            textSize: TextSize.XXSMALL,
            iconSize: IconSize.SIZE_ICON_12,
            iconClasses: isDisabled ? 'content-disabled' : isActive ? 'content-primary' : 'content-ai',
            gap: 1,
          }}
          size={PILL_SIZE.XSMALL}
          background={PILL_BACKGROUND_COLOR.NONE}
          isFlat={true}
          isDisabled={item.disabled}
          className={buildHybridPillClassName(isActive)}
        />
      );
    }

    if (item.topRightIcon) {
      return (
        <Pill
          content={{
            type: PILL_CONTENT_TYPE.ICON,
            icon: item.topRightIcon,
            size: IconSize.SIZE_ICON_12,
          }}
          size={PILL_SIZE.XSMALL}
          background={PILL_BACKGROUND_COLOR.NONE}
          isFlat={true}
          className={buildIconPillClassName(isActive)}
        />
      );
    }

    return null;
  }

  const getGraphicBlock = (shouldRender: boolean, item: ContextualMenuItem): ReactElement | null => {
    if (!shouldRender) {
      return null;
    }
    return getItemGraphic(item);
  };

  const getDescriptionBlock = (description: string | undefined, isActive: boolean, isDisabled?: boolean): ReactElement | null => {
    if (!description) {
      return null;
    }
    return <Text val={description} size={TextSize.XXSMALL} className={isDisabled ? 'content-disabled' : isActive ? 'content-primary' : ''} />;
  };

  const getCard = (item: ContextualMenuItem, groupIndex: number, itemIndex: number): ReactElement => {
    const isActive = item.active;
    const isDisabled = item.disabled ?? false;
    const hasGraphic = 'graphicType' in item && item.graphicType !== ContextualMenuItemGraphicType.NONE;

    const onClick = (isItemActive: boolean, isItemDisabled: boolean): void => {
      if (!isItemActive && !isItemDisabled) {
        onItemClick(groupIndex, itemIndex);
      }
    };

    const cardClassName = buildCardClassName(isActive, isDisabled);
    const labelClassName = buildLabelClassName(isActive, isDisabled);

    return (
      <ClickableDiv key={item.value} className={cardClassName} onClick={onClick.bind(null, isActive, isDisabled)} aria-disabled={isDisabled}>
        {getGraphicBlock(hasGraphic, item)}

        <div className="flex-row-align-center flex-justify-between">
          <div className="flexbox gap-2 flex-align-items-center">
            <Text bold={true} val={item.label} size={TextSize.XXSMALL} className={labelClassName} />
          </div>

          {renderRightPill(item)}
        </div>

        {getDescriptionBlock(item.description, isActive, isDisabled)}
      </ClickableDiv>
    );
  };

  const getCheckbox = (item: ContextualMenuItem, groupIndex: number, itemIndex: number): ReactElement => {
    const graphic = 'graphicType' in item && item.graphicType === ContextualMenuItemGraphicType.ICON ? (item as ContextualMenuItemWithIcon).icon : undefined;

    return (
      <div key={item.value}>
        <LabelledCheckbox
          isChecked={item.active}
          label={item.label}
          labelIcon={graphic}
          onChange={() => {
            onItemClick(groupIndex, itemIndex);
          }}
          textClassName={styles.checkboxLabel}
          labelIconClassName={styles.checkboxIcon}
          textSize={TextSize.XXSMALL}
        />
      </div>
    );
  };

  const getGroupItems = (group: ContextualMenuGroup, groupIndex: number): ReactElement => {
    switch (group.type) {
      case ContextualMenuGroupType.PILLS:
        return <div className={styles.contextualMenuPillContent}>{group.items.map((item, itemIndex) => getPillButton(item, groupIndex, itemIndex))}</div>;
      case ContextualMenuGroupType.CARDS:
        return <div className={'flexbox flex-column gap-2'}>{group.items.map((item, itemIndex) => getCard(item, groupIndex, itemIndex))}</div>;
      case ContextualMenuGroupType.CHECKBOXES:
        return <div className={styles.contextualMenuContainer}>{group.items.map((item, itemIndex) => getCheckbox(item, groupIndex, itemIndex))}</div>;
      default:
        return assertNever(group.type);
    }
  };

  const getGroup = (group: ContextualMenuGroup, groupIndex: number): ReactElement => {
    return (
      <div key={groupIndex} className={'flexbox flex-column gap-2'}>
        {getGroupHeading(group)}
        {getGroupItems(group, groupIndex)}
      </div>
    );
  };

  return <>{groups.map((group, index) => getGroup(group, index))}</>;
}
